import { useCallback, useRef, useState } from 'react';
import { enqueueSnackbar } from 'notistack';
import { ApiService } from 'api';
import { ModelsStatusDropdown } from 'dashboard409a/components/ModelsTable/ModelsStatusDropdown/ModelsStatusDropdown';
import { StyledModelStatusChip } from 'dashboard409a/components/ModelsTable/ModelsTable.styles';
import { StyledSpanEmptyStatus } from 'dashboard409a/components/ValuationsTable/common';
import { useCellEditEnd } from 'dashboard409a/hooks/useCellEditEnd';
import { modelStatusMap } from 'dashboard409a/pages/Operations/components/ModelStatus/ModelStatus.constants';
import { ModelStatus } from 'dashboard409a/pages/Workboard/common/types';
import { getModelStatusString, ModelWithValuationData } from 'dashboard409a/services/models.types';

interface WorkboardModelStatusCellProps {
  modelRow: ModelWithValuationData;
  onStatusUpdate: (modelId: number, newStatus: number) => void;
  cell?: unknown;
  disabled?: boolean;
}

const WorkboardModelStatusCell = ({ modelRow, onStatusUpdate }: WorkboardModelStatusCellProps) => {
  const [isEditingStatus, setIsEditingStatus] = useState(false);
  const [isUpdatingStatus, setIsUpdatingStatus] = useState(false);
  const statusCellRef = useRef<HTMLDivElement>(null);

  const isCompleteStatus = modelRow.model_status === modelStatusMap[ModelStatus.COMPLETE];

  const handleStatusEditEnd = useCellEditEnd(statusCellRef, () => setIsEditingStatus(false));

  const handleStatusClick = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    if (!isUpdatingStatus && !isCompleteStatus) {
      setIsEditingStatus(true);
    }
  }, [isUpdatingStatus, isCompleteStatus]);

  const handleStatusChange = useCallback(async (newStatus: string) => {
    try {
      setIsUpdatingStatus(true);
      const apiStatusValue = modelStatusMap[newStatus as keyof typeof modelStatusMap];
      if (apiStatusValue === undefined) {
        enqueueSnackbar('Invalid status selected', { variant: 'error' });
        return;
      }
      await ApiService.apiDashboardCompanyOperationsFulfillmentPartialUpdate(
        modelRow.company_id.toString(),
        modelRow.attio_valuation_id,
        { model_status: apiStatusValue, company_measurement_date: modelRow.company_measurement_date }
      );
      if (modelRow.id) {
        onStatusUpdate(modelRow.id, apiStatusValue);
      }
      setIsEditingStatus(false);
    } catch {
      enqueueSnackbar('Failed to update model status', { variant: 'error' });
    } finally {
      setIsUpdatingStatus(false);
    }
  }, [modelRow, onStatusUpdate]);

  const statusString = getModelStatusString(modelRow.model_status || 0);
  const statusOption = { value: statusString, label: statusString };

  return (
    <div ref={statusCellRef} style={{ width: '100%' }}>
      <ModelsStatusDropdown
        currentStatus={statusOption}
        onStatusChange={handleStatusChange}
        isEditing={isEditingStatus}
        onEditEnd={handleStatusEditEnd}
      />
      {!isEditingStatus && (
        modelRow.model_status === undefined ? (
          <StyledSpanEmptyStatus
            onClick={handleStatusClick}
            style={{ cursor: 'pointer', fontStyle: 'italic' }}>
            {isUpdatingStatus ? 'Updating...' : 'Click to select status'}
          </StyledSpanEmptyStatus>
        ) : (
          <StyledModelStatusChip
            label={isUpdatingStatus ? 'Updating...' : statusString}
            status={statusString}
            onClick={handleStatusClick}
            style={{
              cursor: isUpdatingStatus || isCompleteStatus ? 'not-allowed' : 'pointer',
              opacity: isUpdatingStatus ? 0.6 : 1,
            }}
            title={isCompleteStatus ? 'Complete status cannot be changed' : undefined}
          />
        )
      )}
    </div>
  );
};

export default WorkboardModelStatusCell;
